import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Panel} from 'react-bootstrap';
import Button from '@material-ui/core/Button';
import {isEmpty} from 'lodash/lang';
import {CommonModal} from 'components/common';
import ActivatedSubscriptionTable from './ActivatedSubscriptionTable';
import PendingSubscriptionTable from './PendingSubscriptionTable';
import SubscriptionCreation from './SubscriptionCreation';
import SubscriptionUpdate from './SubscriptionUpdate';
import SubscriptionActivation from './SubscriptionActivation';
import {
  deleteSubscription,
  markSubscriptionExpired,
  resendSubscription,
  manuallyActivateSubscription,
} from 'api/subscriptions';
import {
  fetchActivatedSubscriptions,
  setCurrentActivatedSubscriptionPage,
  clearActivatedSubscriptionsAfterPage,
  fetchPendingSubscriptions,
  setCurrentPendingSubscriptionPage,
  clearPendingSubscriptionsAfterPage,
} from 'actions/subscriptions';
import {formatErrors} from 'actions/errors';
import {addFlashMessage} from 'actions/flash-message';
import {parseIfIsNumber} from 'utils/common';
import {isKluwellAdmin} from 'utils/permissions';

class SubscriptionList extends React.Component {
  static propTypes = {
    schoolId: PropTypes.number,
    classroomId: PropTypes.number,
    activatedSubscriptionPages: PropTypes.object.isRequired,
    pendingSubscriptionPages: PropTypes.object.isRequired,
    onSubscriptionCountUpdate: PropTypes.func,
    hideSchool: PropTypes.bool.isRequired,
  };
  static defaultProps = {
    hideSchool: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      createModalShown: false,
      updateModalShown: false,
      activateModalShown: false,
      pendingTableKey: 0,
      activatedTableKey: 0,
      currentSubscription: null,
    };
    this.showModal = this.showModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.updateSubscriptionCount = this.updateSubscriptionCount.bind(this);
    this.handleCreationSuccess = this.handleCreationSuccess.bind(this);
    this.handleUpdateSuccess = this.handleUpdateSuccess.bind(this);
    this.handleActivationSuccess = this.handleActivationSuccess.bind(this);
    this.deleteSubscription = this.deleteSubscription.bind(this);
    this.markSubscriptionExpired = this.markSubscriptionExpired.bind(this);
    this.resendSubscription = this.resendSubscription.bind(this);
    this.activateSubscription = this.activateSubscription.bind(this);
  }

  showModal(type, currentSubscription) {
    this.setState({
      [`${type}ModalShown`]: true,
      currentSubscription,
    });
  }

  closeModal() {
    this.setState({
      createModalShown: false,
      updateModalShown: false,
      activateModalShown: false,
      currentSubscription: null,
    });
  }

  updateSubscriptionCount() {
    const {onSubscriptionCountUpdate} = this.props;
    if (onSubscriptionCountUpdate) {
      onSubscriptionCountUpdate();
    }
  }

  handleCreationSuccess() {
    this.setState((prevState) => ({
      createModalShown: false,
      pendingTableKey: prevState.pendingTableKey + 1,
    }));
    this.updateSubscriptionCount();
  }

  handleUpdateSuccess() {
    this.setState((prevState) => {
      const {
        currentSubscription: {status},
      } = prevState;
      const newState = {
        updateModalShown: false,
        currentSubscription: null,
      };
      if (status === 'activated') {
        newState.activatedTableKey = prevState.activatedTableKey + 1;
      } else {
        newState.pendingTableKey = prevState.pendingTableKey + 1;
      }
      return newState;
    });
  }

  handleActivationSuccess() {
    this.setState((prevState) => ({
      activateModalShown: false,
      currentSubscription: null,
      activatedTableKey: prevState.activatedTableKey + 1,
      pendingTableKey: prevState.pendingTableKey + 1,
    }));
    this.updateSubscriptionCount();
  }

  deleteSubscription(record, tableKey) {
    const {addFlashMessage, formatErrors} = this.props;
    return deleteSubscription(record.id)
      .then(() => {
        this.setState((prevState) => ({
          [tableKey]: prevState[tableKey] + 1,
        }));
        addFlashMessage('The subscription has been successfully deleted!', 'success');
        this.updateSubscriptionCount();
      })
      .catch((err) => formatErrors(err));
  }

  markSubscriptionExpired(record, tableKey) {
    const {addFlashMessage, formatErrors} = this.props;
    if (record.isActive === false) {
      addFlashMessage('The subscription is expired already!', 'success');
      return;
    }
    return markSubscriptionExpired(record.id)
      .then(() => {
        this.setState((prevState) => ({
          [tableKey]: prevState[tableKey] + 1,
        }));
        addFlashMessage('The subscription has been marked expired!', 'success');
      })
      .catch((err) => formatErrors(err));
  }

  resendSubscription(record) {
    const {addFlashMessage, formatErrors} = this.props;
    addFlashMessage('Resending...', 'success');
    resendSubscription(record.id)
      .then(() => {
        addFlashMessage('Subscription email resent successfully!', 'success');
      })
      .catch((err) => formatErrors(err));
  }

  activateSubscription(record) {
    const {addFlashMessage, formatErrors} = this.props;
    addFlashMessage('Activating...', 'success');
    return manuallyActivateSubscription({subscriptionId: record.id})
      .then(() => {
        addFlashMessage('Subscription has been successfully activated!', 'success');
        this.handleActivationSuccess();
      })
      .catch((err) => formatErrors(err));
  }

  render() {
    const {
      createModalShown,
      updateModalShown,
      activateModalShown,
      pendingTableKey,
      activatedTableKey,
      currentSubscription,
    } = this.state;
    const {
      role,
      schoolId,
      classroomId,
      activatedSubscriptionPages,
      fetchActivatedSubscriptions,
      setCurrentActivatedSubscriptionPage,
      clearActivatedSubscriptionsAfterPage,
      pendingSubscriptionPages,
      fetchPendingSubscriptions,
      setCurrentPendingSubscriptionPage,
      clearPendingSubscriptionsAfterPage,
      hideSchool,
    } = this.props;
    const canEditActivatedSubscription = isKluwellAdmin(role);
    return (
      <div className="container content-box">
        <CommonModal title="Create New Subscription" onClose={this.closeModal} show={createModalShown}>
          <SubscriptionCreation
            schoolId={schoolId}
            classroomId={classroomId}
            onSubmitSuccess={this.handleCreationSuccess}
          />
        </CommonModal>
        <CommonModal title="Edit Subscription" onClose={this.closeModal} show={updateModalShown}>
          <SubscriptionUpdate
            schoolId={schoolId}
            classroomId={classroomId}
            subscription={currentSubscription}
            onSubmitSuccess={this.handleUpdateSuccess}
          />
        </CommonModal>
        <CommonModal title="Activate Subscription Information" onClose={this.closeModal} show={activateModalShown}>
          {!isEmpty(currentSubscription) && (
            <SubscriptionActivation subscription={currentSubscription} onSubmitSuccess={this.handleActivationSuccess} />
          )}
        </CommonModal>
        <Panel>
          <h3 className="section-header">
            <div className="section-title">Activated Subscriptions</div>
          </h3>
          <ActivatedSubscriptionTable
            {...{
              refreshTableKey: activatedTableKey,
              recordPages: activatedSubscriptionPages,
              fetchRecords: (q, page, perPage, sortOpts, extraFilters = {}) =>
                fetchActivatedSubscriptions(q, page, perPage, sortOpts, {
                  ...extraFilters,
                  summary: true,
                  schoolId,
                  classroomId,
                }),
              setCurrentRecordPage: setCurrentActivatedSubscriptionPage,
              clearRecordsAfterPage: clearActivatedSubscriptionsAfterPage,
              showUpdateModal: canEditActivatedSubscription ? (record) => this.showModal('update', record) : null,
              onDeleteActionClick: (record) => this.deleteSubscription(record, 'activatedTableKey'),
              markExpired: canEditActivatedSubscription
                ? (record) => this.markSubscriptionExpired(record, 'activatedTableKey')
                : null,
              hiddenFields: hideSchool ? ['school'] : undefined,
              extraFiltersEnabled: true,
            }}
          />
        </Panel>
        <Panel>
          <h3 className="section-header">
            <div className="section-title">Pending Subscriptions</div>
            <Button variant="contained" color="primary" className="pull-right" onClick={() => this.showModal('create')}>
              Create Subscription
            </Button>
          </h3>
          <PendingSubscriptionTable
            {...{
              refreshTableKey: pendingTableKey,
              recordPages: pendingSubscriptionPages,
              fetchRecords: (q, page, perPage, sortOpts, extraFilters = {}) =>
                fetchPendingSubscriptions(q, page, perPage, sortOpts, {
                  ...extraFilters,
                  summary: true,
                  schoolId,
                  classroomId,
                }),
              setCurrentRecordPage: setCurrentPendingSubscriptionPage,
              clearRecordsAfterPage: clearPendingSubscriptionsAfterPage,
              showUpdateModal: (record) => this.showModal('update', record),
              onDeleteActionClick: (record) => this.deleteSubscription(record, 'pendingTableKey'),
              resendSubscription: this.resendSubscription,
              showActivateSubscriptionModal: (record) => this.showModal('activate', record),
              activateSubscription: this.activateSubscription,
              hiddenFields: hideSchool ? ['school'] : undefined,
              extraFiltersEnabled: true,
            }}
          />
        </Panel>
      </div>
    );
  }
}

export default connect(
  (state, ownProps) => ({
    role: state.account.getIn(['user', 'role']),
    schoolId: parseIfIsNumber(ownProps.schoolId),
    classroomId: parseIfIsNumber(ownProps.classroomId),
    activatedSubscriptionPages: state.subscriptions.get('activatedSubscriptionPages').toJS(),
    pendingSubscriptionPages: state.subscriptions.get('pendingSubscriptionPages').toJS(),
  }),
  {
    formatErrors,
    addFlashMessage,
    fetchActivatedSubscriptions,
    setCurrentActivatedSubscriptionPage,
    clearActivatedSubscriptionsAfterPage,
    fetchPendingSubscriptions,
    setCurrentPendingSubscriptionPage,
    clearPendingSubscriptionsAfterPage,
  },
)(SubscriptionList);
